// framework/client/helpers/MessagingClient/MessageArea.js
import { ComponentBase } from "../../../core/ComponentBase.js";
import { MessageBubble } from "./MessageBubble.js";

export class MessageArea extends ComponentBase {
  static styles = `
    .message-area-wrapper {
      flex-grow: 1;
      overflow-y: auto;
      padding: 1rem;
      display: flex;
      flex-direction: column;
      background-color: var(--bs-body-bg);
      scrollbar-width: thin;
      scrollbar-color: var(--bs-secondary-bg) var(--bs-tertiary-bg);
    }
    .message-list-spacer { /* Pushes messages to the bottom initially */
        flex-grow: 1;
    }
    .date-separator {
        text-align: center;
        font-size: 0.75rem;
        color: var(--bs-secondary-color);
        margin: 1rem 0;
        position: relative;
    }
    .date-separator span {
        background-color: var(--bs-body-bg); /* Match chat area background */
        padding: 0 0.5rem;
        position: relative;
        z-index: 1;
    }
    .date-separator::before { /* Line for the separator */
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: 50%;
        height: 1px;
        background-color: var(--bs-border-color-translucent);
        z-index: 0;
    }
    
    .agent-progress-bubble-wrapper { /* NEW */
        display: flex;
        margin-bottom: 0.5rem;
        max-width: 75%;
        justify-content: flex-start; /* Align to left like other agent messages */
        margin-right: auto;
        }
        .agent-progress-bubble { /* NEW */
        padding: 0.75rem 1rem;
        border-radius: 1rem;
        background-color: var(--bs-tertiary-bg); /* A distinct but subtle background */
        color: var(--bs-tertiary-color);
        min-width: 200px; /* Ensure some width */
        }
        .agent-progress-bubble .progress-description { /* NEW */
        font-size: 0.9em;
        margin-bottom: 0.5rem;
        }
        .agent-progress-bubble .progress { /* NEW - Bootstrap progress bar styling */
        height: 10px; /* Slimmer progress bar */
        }
        .agent-progress-bubble .cancel-task-btn { /* NEW */
        font-size: 0.8em;
        margin-top: 0.5rem;
      }
  `;

  constructor(config) {
    super(config);
    this.messages = [];
    this.myContactId = config.myContactId;
    this.isGroupChat = false;
    this.isLoadingOlder = false;
    this.hasOlderMessages = true;
    this.bubblesMap = new Map();
    this._prevButtonVisible = false;
    this.activeAgentTasks = new Map();
    this.boundHandleAgentTaskStarted = this.handleAgentTaskStarted.bind(this);
    this.boundHandleAgentTaskUpdate = this.handleAgentTaskUpdate.bind(this);
    this.applyStyles();
  }

  async initialize() {

    // Our scroll container
    this.container = document.createElement("div");
    this.container.className = "message-area-wrapper";
    this.container.addEventListener("scroll", this.handleScroll.bind(this));

    // A flex‐column inside it
    this.messageList = document.createElement("div");
    this.messageList.className = "d-flex flex-column";
    this.container.appendChild(this.messageList);

    // Spacer to push few messages down
    this.spacer = document.createElement("div");
    this.spacer.className = "message-list-spacer";
    this.messageList.appendChild(this.spacer);

    this.appClient.events.server.on('agent_task_started', this.boundHandleAgentTaskStarted);
    this.appClient.events.server.on('agent_task_update', this.boundHandleAgentTaskUpdate);

    await super.initialize();


  }

  setMessages(messages, isGroupChat, myContactId) {
    this.messages = messages;
    this.isGroupChat = isGroupChat;
    this.myContactId = myContactId;
    if (this.messageList && this.messages.length > 0) { // Store active conversation ID
      this.messageList.dataset.activeConversationId = this.messages[0].conversationId;
    } else if (this.messageList) {
      delete this.messageList.dataset.activeConversationId;
    }

    this.renderMessages();
    this.scrollToBottom(true);

    // Emit initial button state
    requestAnimationFrame(() => {
      const c = this.container;
      const isScrollable = c.scrollHeight > c.clientHeight;
      const pixelsFromNewest = c.scrollHeight - c.scrollTop - c.clientHeight;
      const visible = isScrollable && pixelsFromNewest > 200;
      this._prevButtonVisible = visible;
      this.emit("scrollButtonStateChange", { visible });
    });
  }

  


  addMessage(msg, isPrepend = false) {
    if (isPrepend) this.messages.unshift(msg);
    else this.messages.push(msg);

    this.renderSingleMessage(msg, isPrepend);
    if (!isPrepend) this.scrollToBottom();
  }

  updateMessageStatus(tempId, serverId, status) {
    const bubble = this.bubblesMap.get(tempId);
    if (!bubble) return this.log.warn(`No bubble for ${tempId}`);
    bubble.messageData._id = serverId;
    bubble.messageData.status = status;
    bubble.render();
    if (serverId !== tempId) {
      this.bubblesMap.set(serverId, bubble);
      this.bubblesMap.delete(tempId);
    }
  }

  renderMessages() {
    // Clear
    this.messageList.innerHTML = "";
    this.bubblesMap.forEach(b => b.destroy());
    this.bubblesMap.clear();

    // Re‐add spacer
    this.messageList.appendChild(this.spacer);

    let lastDate = null;
    this.messages.forEach(msg => {
      const ds = new Date(msg.timestamp)
        .toLocaleDateString([], { year: "numeric", month: "long", day: "numeric" });
      if (ds !== lastDate) {
        this.addDateSeparator(ds);
        lastDate = ds;
      }
      this.renderSingleMessage(msg, false);
    });
  }

  renderSingleMessage(msgData, prepend) {
    msgData.isOwnMessage = msgData.senderContactId === this.myContactId;
    const idKey = msgData._id || msgData.tempId || Date.now();
    const bubble = new MessageBubble({
      appClient: this.appClient,
      instanceId: `msgbubble-${idKey}`,
      messageData: msgData,
      isGroupChat: this.isGroupChat
    });
    bubble.initialize();
    const el = bubble.render();

    // Track it
    this.bubblesMap.set(msgData._id || msgData.tempId, bubble);

    // Insert
    if (prepend) {
      // older messages go above the rest, so before the spacer
      this.messageList.insertBefore(el, this.spacer);
    } else {
      // newest messages just append
      this.messageList.appendChild(el);
    }
  }

  addDateSeparator(dateString) {
    const sep = document.createElement("div");
    sep.className = "date-separator";
    sep.innerHTML = `<span>${dateString}</span>`;
    this.messageList.appendChild(sep);
  }

  handleScroll() {
    const c = this.container;

    // 1) trigger load‐older when scrolled to the top
    if (c.scrollTop === 0 && !this.isLoadingOlder && this.hasOlderMessages) {
      this.emit("loadOlderMessages");
    }

    // 2) show/hide scroll‐to‐bottom button
    const pixelsFromNewest = Math.max(0, c.scrollHeight - c.scrollTop - c.clientHeight);
    const showThreshold = 200;
    const hideThreshold = 50;
    let visible;

    if (pixelsFromNewest > showThreshold) {
      visible = true;
    } else if (pixelsFromNewest < hideThreshold) {
      visible = false;
    } else {
      // in the middle, keep last state
      visible = this._prevButtonVisible;
    }
    this._prevButtonVisible = visible;
    this.emit("scrollButtonStateChange", { visible });
  }

  scrollToBottom() {
    if (!this.container) return;
    // Simply set scrollTop to the maximum
    this.container.scrollTop = this.container.scrollHeight;
  }

  setLoadingOlder(isLoading, hasMore) {
    this.isLoadingOlder = isLoading;
    this.hasOlderMessages = hasMore;
    // you can show a spinner at the top if you like
  }

  render() {
    return this.container;
  }

  handleAgentTaskStarted(eventData) {
    // eventData: { taskId, agentContactId, agentName, channelId, description, isCancellable }
    if (eventData.channelId !== this.messageList.dataset.activeConversationId) { // Assuming you set this dataset attr
      return; // Not for the current chat
    }

    // Check if this task was initiated by the current user.
    // This is tricky. We need a way to link the task back to a user's message.
    // For now, we'll assume if it's for the current channel, we show it.
    // A more robust way would be if the agent's *first* response (the "I'm working on it" message)
    // included the taskId, and the client could then associate it.
    // Or, if the 'agent_task_started' event included the original user's message ID.

    const progressBubbleWrapper = document.createElement('div');
    progressBubbleWrapper.className = 'agent-progress-bubble-wrapper';
    progressBubbleWrapper.dataset.taskId = eventData.taskId;

    const progressBubble = document.createElement('div');
    progressBubble.className = 'agent-progress-bubble';

    const descriptionEl = document.createElement('div');
    descriptionEl.className = 'progress-description';
    descriptionEl.textContent = `${eventData.agentName}: ${eventData.description || this.t('Processing your request...')}`;

    const progressBarContainer = document.createElement('div');
    progressBarContainer.className = 'progress';
    const progressBar = document.createElement('div');
    progressBar.className = 'progress-bar progress-bar-striped progress-bar-animated';
    progressBar.role = 'progressbar';
    progressBar.style.width = '0%';
    progressBar.setAttribute('aria-valuenow', '0');
    progressBarContainer.appendChild(progressBar);

    progressBubble.appendChild(descriptionEl);
    progressBubble.appendChild(progressBarContainer);

    if (eventData.isCancellable) {
      const cancelButton = document.createElement('button');
      cancelButton.className = 'btn btn-sm btn-outline-danger cancel-task-btn mt-2';
      cancelButton.textContent = this.t('Cancel Task');
      cancelButton.addEventListener('click', () => {
        // Emit an event or call an RPC to cancel the task on the server
        this.appClient.rpcClient.call('AgentTaskRpc.cancelTask', { taskId: eventData.taskId, agentName: eventData.agentName })
          .catch(err => this.log.error("Failed to send cancel task request:", err));
      });
      progressBubble.appendChild(cancelButton);
    }

    progressBubbleWrapper.appendChild(progressBubble);
    this.messageList.appendChild(progressBubbleWrapper); // Append to message list
    this.scrollToBottom(true);

    this.activeAgentTasks.set(eventData.taskId, {
      element: progressBubbleWrapper,
      descriptionElement: descriptionEl,
      progressBarElement: progressBar,
      cancelButtonElement: eventData.isCancellable ? progressBubble.querySelector('.cancel-task-btn') : null
    });
    this.appClient.tx.applyTranslations(progressBubbleWrapper);
  }

  handleAgentTaskUpdate(eventData) {
    // eventData: { taskId, agentContactId, agentName, channelId, status, progress?, message? }
    if (eventData.channelId !== this.messageList.dataset.activeConversationId) {
      return;
    }

    const taskUI = this.activeAgentTasks.get(eventData.taskId);
    if (!taskUI) return;

    if (eventData.status === 'completed' || eventData.status === 'failed' || eventData.status === 'cancelled') {
      // Task finished, remove the progress bubble after a short delay or when final message arrives
      // For now, let's remove it immediately. A better UX might fade it out or replace with "Completed".
      setTimeout(() => { // Slight delay to allow final message to render first
        taskUI.element.remove();
        this.activeAgentTasks.delete(eventData.taskId);
      }, eventData.status === 'completed' ? 500 : 2000); // Longer delay for errors/cancelled

      if (eventData.status === 'failed' && eventData.message) {
        this.appClient.notifier.error(`${eventData.agentName} task failed: ${eventData.message}`);
      } else if (eventData.status === 'cancelled') {
        this.appClient.notifier.info(`${eventData.agentName} task was cancelled.`);
      }

    } else if (eventData.status === 'in_progress') {
      if (eventData.progress !== undefined) {
        taskUI.progressBarElement.style.width = `${eventData.progress}%`;
        taskUI.progressBarElement.setAttribute('aria-valuenow', eventData.progress);
      }
      if (eventData.message) {
        taskUI.descriptionElement.textContent = `${eventData.agentName}: ${eventData.message}`;
        this.appClient.tx.applyTranslations(taskUI.descriptionElement);
      }
    }
  }


  destroy() {
    this.container.removeEventListener("scroll", this.handleScroll);
    this.bubblesMap.forEach(b => b.destroy());
    this.bubblesMap.clear();

    this.appClient.events.server.off('agent_task_started', this.boundHandleAgentTaskStarted);
    this.appClient.events.server.off('agent_task_update', this.boundHandleAgentTaskUpdate);
    this.activeAgentTasks.forEach(taskUI => taskUI.element.remove());

    super.destroy();
  }
}